import React, { useEffect, useMemo, useRef, useState } from 'react';
import './JourneyMapBoard.scss';
import JourneyMapCard from './JourneyMapCard';
import JourneyMapDividerLayer from './JourneyMapDividerLayer';
import JourneyMapEdgeLayer from './JourneyMapEdgeLayer';
import JourneyMapViewportControls from './JourneyMapViewportControls';
import {
  JOURNEY_MAX_ZOOM,
  JOURNEY_MIN_ZOOM,
  JOURNEY_NODE_HEIGHT,
  JOURNEY_NODE_WIDTH,
  JOURNEY_ZOOM_STEP,
} from '../../../support/journeyMap/constants';
import {
  buildItemMapById,
  clamp,
  getBoardBounds,
  isJourneyItemOverdue,
  normalizeCanvasSize,
  normalizeDelimiters,
  normalizeItems,
  resolveRenderableLinks,
} from '../../../support/journeyMap/normalizers';
import {
  JourneyMapBoardMode,
  JourneyMapRenderableItem,
  JourneyMapRenderableLink,
  JourneyMapSectionDelimiterLike,
} from './journeyMapBoardTypes';

type Props = {
  mode?: JourneyMapBoardMode;
  items?: JourneyMapRenderableItem[];
  links?: JourneyMapRenderableLink[];
  backgroundImageUrl?: string;
  sectionDelimiters?: JourneyMapSectionDelimiterLike[];
  canvasWidth?: number;
  canvasHeight?: number;
  selectedItemId?: string | number | null;
  completionByItemId?: Record<string | number, boolean>;
  pendingValidationByItemId?: Record<string | number, boolean>;
  syncStatusByItemId?: Record<string | number, string | null>;
  notApplicableByItemId?: Record<string | number, boolean>;
  lockedByItemId?: Record<string | number, boolean>;
  overdueByItemId?: Record<string | number, boolean>;
  onSelectItem?: (item: JourneyMapRenderableItem) => void;
  minHeight?: number | string;
  autoCenterSelectedItem?: boolean;
};

const JourneyMapBoard: React.FC<Props> = ({
                                            mode = 'viewer',
                                            items = [],
                                            links = [],
                                            backgroundImageUrl = '',
                                            sectionDelimiters = [],
                                            canvasWidth,
                                            canvasHeight,
                                            selectedItemId = null,
                                            completionByItemId = {},
                                            pendingValidationByItemId = {},
                                            syncStatusByItemId = {},
                                            notApplicableByItemId = {},
                                            lockedByItemId = {},
                                            overdueByItemId = {},
                                            onSelectItem,
                                            minHeight = 700,
                                            autoCenterSelectedItem,
                                          }) => {
  const viewportRef = useRef<HTMLDivElement | null>(null);
  const [zoom, setZoom] = useState(1);
  const hasInitiallyCenteredRef = useRef(false);

  const logicalCanvas = useMemo(() => {
    return normalizeCanvasSize(canvasWidth, canvasHeight);
  }, [canvasWidth, canvasHeight]);

  const normalizedItems = useMemo(() => {
    const normalized = normalizeItems(items);

    return normalized.map((item: any) => {
      const providerRaw = String(item.provider || '').trim();
      const providerLabel = providerRaw
        ? providerRaw.replace(/_/g, ' ').replace(/\b\w/g, (c: string) => c.toUpperCase())
        : '';

      const resolvedCourseName = String(item.resolvedReferencedTitle || '').trim();

      return {
        ...item,
        description: resolvedCourseName || item.description || '',
        provider: providerLabel || item.provider || '',
      };
    });
  }, [items]);

  const normalizedDelimiters = useMemo(() => {
    return normalizeDelimiters(sectionDelimiters);
  }, [sectionDelimiters]);

  const bounds = useMemo(() => {
    return getBoardBounds(
      logicalCanvas.width,
      logicalCanvas.height,
      normalizedItems,
      normalizedDelimiters,
    );
  }, [logicalCanvas, normalizedItems, normalizedDelimiters]);

  const itemById = useMemo(() => {
    return buildItemMapById(normalizedItems);
  }, [normalizedItems]);

  const resolvedLinks = useMemo(() => {
    return resolveRenderableLinks(links, itemById);
  }, [links, itemById]);

  const shouldAutoCenterSelectedItem =
    typeof autoCenterSelectedItem === 'boolean' ? autoCenterSelectedItem : mode === 'viewer';

  const centerBoardContentInitially = () => {
    if (!viewportRef.current || !normalizedItems.length) return;
    if (hasInitiallyCenteredRef.current) return;

    const viewport = viewportRef.current;

    const minLeft = Math.min(...normalizedItems.map((item: any) => item._x));
    const maxRight = Math.max(...normalizedItems.map((item: any) => item._x + JOURNEY_NODE_WIDTH));
    const minTop = Math.min(...normalizedItems.map((item: any) => item._y));
    const maxBottom = Math.max(...normalizedItems.map((item: any) => item._y + JOURNEY_NODE_HEIGHT));

    const contentCenterX = ((minLeft + maxRight) / 2) * zoom;
    const contentCenterY = ((minTop + maxBottom) / 2) * zoom;

    viewport.scrollTo({
      left: Math.max(0, contentCenterX - viewport.clientWidth / 2),
      top: Math.max(0, contentCenterY - viewport.clientHeight / 2),
      behavior: 'auto',
    });

    hasInitiallyCenteredRef.current = true;
  };

  const centerSelectedItem = () => {
    if (!selectedItemId || !viewportRef.current) return;

    const selected = normalizedItems.find((item) => String(item.id) === String(selectedItemId));
    if (!selected) return;

    const viewport = viewportRef.current;

    const scaledItemCenterX = (selected._x + JOURNEY_NODE_WIDTH / 2) * zoom;
    const scaledItemCenterY = (selected._y + JOURNEY_NODE_HEIGHT / 2) * zoom;

    const nextLeft = scaledItemCenterX - viewport.clientWidth / 2;
    const nextTop = scaledItemCenterY - viewport.clientHeight / 2;

    viewport.scrollTo({
      left: Math.max(0, nextLeft),
      top: Math.max(0, nextTop),
      behavior: 'smooth',
    });
  };

  useEffect(() => {
    if (!normalizedItems.length) return;
    centerBoardContentInitially();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [normalizedItems, zoom]);

  useEffect(() => {
    if (!shouldAutoCenterSelectedItem) return;
    if (!selectedItemId) return;
    centerSelectedItem();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedItemId, zoom, normalizedItems, shouldAutoCenterSelectedItem]);

  const handleZoomIn = () => {
    setZoom((prev) =>
      clamp(Number((prev + JOURNEY_ZOOM_STEP).toFixed(2)), JOURNEY_MIN_ZOOM, JOURNEY_MAX_ZOOM),
    );
  };

  const handleZoomOut = () => {
    setZoom((prev) =>
      clamp(Number((prev - JOURNEY_ZOOM_STEP).toFixed(2)), JOURNEY_MIN_ZOOM, JOURNEY_MAX_ZOOM),
    );
  };

  const handleZoomReset = () => {
    setZoom(1);
    hasInitiallyCenteredRef.current = false;
  };

  return (
    <div className="journey-map-board" data-mode={mode} style={{ position: 'relative' }}>
      <JourneyMapViewportControls
        zoom={zoom}
        minZoom={JOURNEY_MIN_ZOOM}
        maxZoom={JOURNEY_MAX_ZOOM}
        onZoomIn={handleZoomIn}
        onZoomOut={handleZoomOut}
        onZoomReset={handleZoomReset}
      />

      <div
        ref={viewportRef}
        className="journey-map-board__viewport"
        style={{
          overflow: 'auto',
          position: 'relative',
          width: '100%',
          maxWidth: '100%',
          minHeight: typeof minHeight === 'number' ? `${minHeight}px` : minHeight,
        }}
      >
        <div
          style={{
            width: `${bounds.width * zoom}px`,
            height: `${bounds.height * zoom}px`,
            position: 'relative',
          }}
        >
          <div
            className="journey-map-board__surface"
            style={{
              width: `${bounds.width}px`,
              height: `${bounds.height}px`,
              position: 'relative',
              transform: `scale(${zoom})`,
              transformOrigin: 'top left',
              backgroundImage: backgroundImageUrl ? `url(${backgroundImageUrl})` : undefined,
              backgroundSize: backgroundImageUrl ? '100% 100%' : undefined,
              backgroundPosition: backgroundImageUrl ? 'center center' : undefined,
              backgroundRepeat: backgroundImageUrl ? 'no-repeat' : undefined,
            }}
          >
            <JourneyMapDividerLayer delimiters={normalizedDelimiters} bounds={bounds} />

            <div
              className="journey-map-board__edges"
              style={{
                position: 'absolute',
                inset: 0,
                zIndex: 2,
                pointerEvents: 'none',
              }}
            >
              <JourneyMapEdgeLayer resolvedLinks={resolvedLinks} itemById={itemById} bounds={bounds} />
            </div>

            <div
              className="journey-map-board__nodes"
              style={{
                position: 'absolute',
                inset: 0,
                zIndex: 4,
                pointerEvents: 'none',
              }}
            >
              {normalizedItems.map((item, idx) => {
                const key = String(item.id);
                const completed = !!completionByItemId[key];
                const pendingValidation = !!pendingValidationByItemId[key];
                const notApplicable = !!notApplicableByItemId[key];
                const progressionLocked = !!lockedByItemId[key];
                const disabled = progressionLocked || notApplicable;
                const overdue =
                  overdueByItemId[key] !== undefined
                    ? !!overdueByItemId[key]
                    : isJourneyItemOverdue(item, completed);

                return (
                  <div
                    key={item.id || idx}
                    data-node-id={key}
                    className="journey-map-board__node"
                    aria-disabled={disabled}
                    data-disabled={disabled ? 'true' : 'false'}
                    data-not-applicable={notApplicable ? 'true' : 'false'}
                    data-locked={progressionLocked ? 'true' : 'false'}
                    data-pending-validation={pendingValidation ? 'true' : 'false'}
                    style={{
                      position: 'absolute',
                      left: `${item._x}px`,
                      top: `${item._y}px`,
                      width: `${JOURNEY_NODE_WIDTH}px`,
                      height: `${JOURNEY_NODE_HEIGHT}px`,
                      zIndex: 4,
                      animationDelay: `${Math.min(idx * 50, 450)}ms`,
                      pointerEvents: 'auto',
                      borderRadius: '12px',
                      overflow: 'visible',
                      background: 'transparent',
                      opacity: disabled ? 0.8 : 1,
                    }}
                  >
                    <div
                      style={{
                        width: '100%',
                        height: '100%',
                        borderRadius: '12px',
                        overflow: 'hidden',
                        background: 'transparent',
                      }}
                    >
                      <JourneyMapCard
                        item={item}
                        completed={completed}
                        pendingValidation={pendingValidation}
                        syncStatus={syncStatusByItemId[key] ?? item?.sync_status ?? null}
                        locked={disabled}
                        overdue={overdue}
                        active={String(selectedItemId) === key && !disabled}
                        notApplicable={notApplicable}
                        onClick={() => {
                          if (disabled) return;
                          onSelectItem?.(item);
                        }}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default JourneyMapBoard;